import { useTranslation } from 'react-i18next';
import { MEASUREMENT_INFO, fmtCurrency, shortLocation, hubTradeForPayTrade } from '../utils/tradePayData';
import TradeCrossLinks from './TradeCrossLinks';
import useIsPhone from '../utils/useIsPhone';
import UnionScaleBlock from './UnionScaleBlock';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

// Keeps a percent-position label from clipping off the edge of its container when its point
// sits right at 0% or 100% -- the label itself still centers normally in the middle range.
const clampPct = (p) => Math.min(94, Math.max(6, p));

// Phone (<= 640px) text overrides: small labels up to readable sizes, helper text lighter.
// Layout and figures are unchanged; desktop/tablet styles are untouched.
const PHONE_TEXT = '0.875rem';
const PHONE_LABEL = '0.75rem';
const PHONE_HELPER = 'rgba(255,255,255,0.68)';

// Horizontal range bar spanning the leftmost to rightmost present figure (entry->top, or
// entry->median if top is missing, etc.) with a marker + callout for every point in between
// (just the median today, since trade_career_data only ever has up to 3 columns). Renders
// nothing above/below the endpoints -- the leftmost point IS 0% and the rightmost IS 100% by
// construction, so this never implies a fixed dollar scale, only relative spread.
function RangeBar({ points, chipColor, isMobile }) {
  const isPhone = useIsPhone();
  const pointLabel = { fontSize: isPhone ? PHONE_LABEL : '0.6rem', fontWeight: 700, color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.04em' };
  const left = points[0];
  const right = points[points.length - 1];
  const span = right.value - left.value;
  const middlePoints = points.slice(1, -1).map(p => ({
    ...p,
    pct: clampPct(span > 0 ? ((p.value - left.value) / span) * 100 : 50),
  }));

  return (
    <div style={{ padding: '4px', marginBottom: '4px' }}>
      {/* Fixed-height row for the median (etc.) callouts, anchored to its own bottom edge --
          guarantees a clean gap above the track regardless of font-metric rounding, instead of
          relying on a negative-offset guess that can crowd into the marker dot below it. */}
      {middlePoints.length > 0 && (
        <div style={{ position: 'relative', height: '44px' }}>
          {middlePoints.map(p => (
            <div key={p.key} style={{
              position: 'absolute', left: `${p.pct}%`, bottom: 0, transform: 'translateX(-50%)',
              textAlign: 'center', width: 'max-content', maxWidth: isMobile ? '100px' : '160px',
            }}>
              <p style={{ fontFamily: FONT_HEAD, fontSize: isMobile ? '1rem' : '1.2rem', color: WHITE, letterSpacing: '0.02em', lineHeight: 1.1 }}>
                {fmtCurrency(p.value)}
              </p>
              <p style={pointLabel}>
                {p.label}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Track */}
      <div style={{ position: 'relative', height: '8px', marginTop: middlePoints.length > 0 ? '10px' : 0, borderRadius: '999px', backgroundColor: `${chipColor}22`, border: `1px solid ${chipColor}44`, overflow: 'visible' }}>
        <div style={{ position: 'absolute', inset: 0, borderRadius: '999px', backgroundColor: chipColor, opacity: 0.4 }} />
        {middlePoints.map(p => (
          <div key={p.key} style={{
            position: 'absolute', left: `${p.pct}%`, top: '50%', transform: 'translate(-50%, -50%)',
            width: '14px', height: '14px', borderRadius: '50%', backgroundColor: WHITE,
            border: `3px solid ${chipColor}`, boxShadow: '0 0 0 2px ' + NAVY_MID,
          }} />
        ))}
      </div>

      {/* Endpoint labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', gap: '10px' }}>
        <div style={{ textAlign: 'left' }}>
          <p style={{ fontFamily: FONT_HEAD, fontSize: isMobile ? '1rem' : '1.2rem', color: WHITE, letterSpacing: '0.02em', lineHeight: 1.1 }}>
            {fmtCurrency(left.value)}
          </p>
          <p style={pointLabel}>
            {left.label}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontFamily: FONT_HEAD, fontSize: isMobile ? '1rem' : '1.2rem', color: WHITE, letterSpacing: '0.02em', lineHeight: 1.1 }}>
            {fmtCurrency(right.value)}
          </p>
          <p style={pointLabel}>
            {right.label}
          </p>
        </div>
      </div>
    </div>
  );
}

// A single confirmed figure with nothing to compare it against (e.g. HVAC Technician or
// Carpenter, which only have median_pay) -- shown as one marked point, deliberately not a
// full-width bar, since a bar implies a range that doesn't exist here.
function SinglePoint({ point, chipColor, isMobile }) {
  const isPhone = useIsPhone();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px',
      backgroundColor: NAVY, borderRadius: '8px', border: `1px solid ${chipColor}33`, marginBottom: '4px',
    }}>
      <div style={{
        width: '14px', height: '14px', borderRadius: '50%', backgroundColor: WHITE,
        border: `3px solid ${chipColor}`, flexShrink: 0,
      }} />
      <div>
        <p style={{ fontSize: isPhone ? PHONE_LABEL : '0.64rem', fontWeight: 700, color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>
          {point.label}
        </p>
        <p style={{ fontFamily: FONT_HEAD, fontSize: isMobile ? '1.15rem' : '1.4rem', color: WHITE, letterSpacing: '0.02em' }}>
          {fmtCurrency(point.value)}
        </p>
      </div>
    </div>
  );
}

// Renders trade_career_data.career_path (a "Step -> Step -> Step" string, distinct from
// trade_resources.career_path shown elsewhere) as a stepped visual instead of a flat sentence.
// Each step+arrow is grouped into one flex item so wrapping onto a new row never orphans an
// arrow alone at the start of a line -- it breaks after a step, never before one.
//
// This card renders at wildly different container widths depending on context -- a full-width
// single-trade panel (~600-700px), a real mobile viewport (~350px), or a Career Pay Guide
// compare-mode grid column (~175-350px, which is narrower than either of the other two and
// isn't tied to the page/viewport width at all). Rather than branch on an isMobile prop (which
// only ever reflects viewport width, not this component's actual rendered width), each pill
// is deliberately allowed to WRAP its own text and shrink below its unwrapped width -- so it
// naturally reflows to fit whatever width the flex container actually has, at any container
// width, without measuring anything. (No whiteSpace:'nowrap' here on purpose -- that's what
// previously forced every pill to its full unwrapped width and let it overflow a narrow
// compare column into the neighboring card.)
function CareerPathLadder({ path }) {
  // The final step sometimes carries a trailing sentence period (e.g. "...Consultant.") --
  // strip it so the last pill doesn't end with a stray dot.
  const steps = path.split('->').map(s => s.trim().replace(/\.$/, '')).filter(Boolean);
  if (steps.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', rowGap: '10px' }}>
      {steps.map((step, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '100%', minWidth: 0 }}>
          <span style={{
            backgroundColor: NAVY, border: '1px solid rgba(255,255,255,0.14)', borderRadius: '8px',
            padding: '7px 12px', fontSize: '0.8rem', fontWeight: 600, color: WHITE,
            maxWidth: '100%', overflowWrap: 'break-word', wordBreak: 'break-word',
          }}>
            {step}
          </span>
          {i < steps.length - 1 && (
            <span style={{ color: AMBER, fontSize: '1rem', fontWeight: 700, marginRight: '8px', flexShrink: 0 }}>→</span>
          )}
        </div>
      ))}
    </div>
  );
}

// Full pay-detail card for a single trade_career_data row -- shared by Career Pay Guide's
// detail panel and the Trade Hub's pay section so both render identical wage figures/badges.
// onViewTradeHub: optional -- if provided and this trade is one of the 9 canonical Trade Hub
// trades, shows a "View full trade guide" link. Omitted inside the Hub itself (no self-link).
// compact: the card is a Career Pay Guide compare column -- the union block uses its compact form.
// unionBlockOffset: px of space above the union block, set by the compare view so the blocks in
// one row of columns start on the same line.
export default function TradePayDetailCard({ row, isMobile = false, onViewTradeHub = null, compact = false, unionBlockOffset = 0 }) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  // A DB CHECK constraint currently restricts measurement_type to the two keys below, but that
  // constraint lives outside this codebase -- fall back to the percentile framing (the more
  // common of the two) rather than crashing on `undefined.columns` if that ever changes.
  const info = MEASUREMENT_INFO[row.measurement_type] || MEASUREMENT_INFO.percentile_10_50_90;
  // Some rows (e.g. HVAC Technician, Carpenter) only have median_pay confirmed -- entry/top
  // were left null rather than estimated. Only render the points that actually have a value,
  // and flag it below rather than leaving a blank/broken-looking bar.
  const presentColumns = info.columns.filter(col => row[col.key] != null);
  const hasMissingColumns = presentColumns.length < info.columns.length;
  const points = presentColumns.map(col => ({ ...col, value: row[col.key], label: t(col.labelKey) }));
  // Trades outside the 9 canonical Trade Hub trades get the lighter inline cross-links
  // instead of a "View full trade guide" link -- computed independent of onViewTradeHub so
  // the Hub itself (which never passes onViewTradeHub) correctly never shows either one.
  const canonicalEntry = hubTradeForPayTrade(row.trade);
  const hubEntry = onViewTradeHub ? canonicalEntry : null;

  return (
    <div style={{ backgroundColor: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: compact && !isPhone ? '14px' : '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
        <p style={{ fontWeight: 700, fontSize: '1.1rem', color: WHITE, lineHeight: 1.3 }}>{row.trade}</p>
        {row.is_proxy_soc && (
          <span
            title={t('resources.payCard.estimatedTooltip', { socSuffix: row.soc_code ? ` (SOC ${row.soc_code})` : '' })}
            style={{
              backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)',
              border: '1px solid rgba(255,255,255,0.25)', borderRadius: '999px',
              fontSize: '0.66rem', fontWeight: 700, padding: '4px 10px', whiteSpace: 'nowrap',
              cursor: 'help', flexShrink: 0,
            }}
          >
            {t('resources.payCard.estimatedBadge')}
          </span>
        )}
      </div>
      <p style={{ fontSize: isPhone ? PHONE_TEXT : '0.78rem', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.4)', marginBottom: '18px' }}>
        {shortLocation(row)}{row.metro_area ? ' metro' : ' (statewide)'}
      </p>

      {/* Measurement-type chip -- visually distinct color/icon/heading per type,
          never just a relabeled version of the other. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <span style={{
          fontFamily: FONT_HEAD, fontSize: '1rem', letterSpacing: '0.03em', color: WHITE,
        }}>
          {t(info.headingKey)}
        </span>
        <span style={{
          backgroundColor: `${info.chipColor}22`, color: info.chipColor,
          border: `1px solid ${info.chipColor}55`, borderRadius: '999px',
          fontSize: isPhone ? '0.8rem' : '0.68rem', fontWeight: 700, padding: '3px 10px',
        }}>
          {t(info.chipLabelKey)}
        </span>
      </div>

      {points.length === 0 ? (
        <p style={{ fontSize: isPhone ? PHONE_TEXT : '0.82rem', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.4)', marginBottom: '20px', fontStyle: 'italic' }}>
          {t('resources.payCard.noWageData')}
        </p>
      ) : points.length === 1 ? (
        <div style={{ marginBottom: (info.note || hasMissingColumns) ? '10px' : '20px' }}>
          <SinglePoint point={points[0]} chipColor={info.chipColor} isMobile={isMobile} />
        </div>
      ) : (
        <div style={{ marginBottom: (info.note || hasMissingColumns) ? '14px' : '24px' }}>
          <RangeBar points={points} chipColor={info.chipColor} isMobile={isMobile} />
        </div>
      )}

      {points.length > 0 && hasMissingColumns && (
        <p style={{ fontSize: isPhone ? PHONE_TEXT : '0.72rem', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.4)', lineHeight: 1.5, marginBottom: '20px', fontStyle: 'italic' }}>
          {t('resources.payCard.missingColumnsNote', { count: points.length })}
        </p>
      )}

      {info.noteKey && (
        <p style={{ fontSize: isPhone ? PHONE_TEXT : '0.72rem', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.4)', lineHeight: 1.5, marginBottom: '20px', fontStyle: 'italic' }}>
          {t(info.noteKey)}
        </p>
      )}

      {/* Union journeyman scale beside the government median -- additive; renders nothing for
          trades without a clear union match. */}
      <div data-union-slot style={unionBlockOffset ? { paddingTop: `${unionBlockOffset}px` } : undefined}>
        <UnionScaleBlock
          row={row}
          stacked={isMobile}
          compact={compact}
          medianLabel={t((info.columns.find(c => c.key === 'median_pay') || {}).labelKey || 'resources.measurement.median')}
        />
      </div>

      {row.job_description && (
        <p style={{ fontSize: isPhone ? '0.9rem' : '0.82rem', color: isPhone ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.6)', lineHeight: 1.55, marginBottom: '20px' }}>
          {row.job_description}
        </p>
      )}

      {row.career_path && (
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontSize: isPhone ? PHONE_LABEL : '0.66rem', fontWeight: 700, color: AMBER, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '10px' }}>
            {t('resources.careerPath')}
          </p>
          <CareerPathLadder path={row.career_path} />
        </div>
      )}

      {row.growth_outlook && (
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontSize: isPhone ? PHONE_LABEL : '0.66rem', fontWeight: 700, color: AMBER, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>
            {t('resources.payCard.jobGrowthOutlook')}
          </p>
          <p style={{ fontSize: isPhone ? '0.9rem' : '0.84rem', color: WHITE, lineHeight: 1.5 }}>
            {row.growth_outlook}
          </p>
        </div>
      )}

      {row.typical_job_titles && row.typical_job_titles.length > 0 && (
        <div style={{ marginBottom: '18px' }}>
          <p style={{ fontSize: isPhone ? PHONE_LABEL : '0.66rem', fontWeight: 700, color: AMBER, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '8px' }}>
            {t('resources.payCard.typicalJobTitles')}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {row.typical_job_titles.map(title => (
              <span key={title} style={{
                backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.8)',
                border: '1px solid rgba(255,255,255,0.12)', borderRadius: '999px',
                fontSize: isPhone ? PHONE_TEXT : '0.76rem', padding: '5px 12px',
              }}>
                {title}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Previously gated on !canonicalEntry, so the 9 TRADE_HUB_TRADES showed only a "View
          full trade guide" button instead. That gate now hides the section from trades whose
          resource_directory_categories mapping is both populated and more current than the hub's
          own single `category` constant -- Heavy Equipment Operator maps to "Equipment" +
          "Operating Engineers" here, while TRADE_HUB_TRADES still points it at "Heavy Equipment",
          a category no active row uses. Rendering for every trade keeps the two from diverging. */}
      <TradeCrossLinks trade={row.trade} categories={row.resource_directory_categories} />

      {(row.data_source || row.data_vintage) && (
        <p style={{ fontSize: isPhone ? PHONE_TEXT : '0.68rem', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.3)', borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '12px', marginBottom: hubEntry ? '12px' : 0 }}>
          {t('resources.payCard.source', { value: [row.data_source, row.data_vintage].filter(Boolean).join(', ') })}
        </p>
      )}

      {hubEntry && (
        <button
          onClick={() => onViewTradeHub(hubEntry.hubTrade)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '0.8rem',
            color: AMBER, fontWeight: 700, background: 'none', border: 'none', padding: 0,
            cursor: 'pointer', fontFamily: FONT_BODY, textDecoration: 'underline', textUnderlineOffset: '2px',
            marginTop: (row.data_source || row.data_vintage) ? 0 : '4px',
            ...(isPhone ? { minHeight: '44px', fontSize: '0.9rem' } : {}),
          }}
        >
          {t('resources.viewFullTradeGuide')}
        </button>
      )}
    </div>
  );
}
